import { color } from '../styles/theme';

/**
 * A segmented switch between two views of one section.
 *
 * Used where the sidebar used to carry two entries for what is really one
 * destination — Messages (general inquiries · requests) and Dashboard
 * (overview · reporting). The count rides on the tab so the badge the sidebar
 * used to show is not lost in the consolidation.
 */

export interface SectionTab<T extends string> {
  key: T;
  label: string;
  /** Unactioned rows behind this tab. Zero is not shown — it is not news. */
  badge?: number;
}

export function SectionTabs<T extends string>({
  value,
  onChange,
  tabs,
  compact = false,
}: {
  value: T;
  onChange: (next: T) => void;
  tabs: ReadonlyArray<SectionTab<T>>;
  /** Tighter tabs, for a row of saved views that has to fit one line. */
  compact?: boolean;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        gap: 3,
        padding: 3,
        borderRadius: 12,
        background: color.bg,
        border: `1px solid ${color.line}`,
        alignSelf: 'flex-start',
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      {tabs.map((t) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: 'none',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              borderRadius: 9,
              padding: compact ? '7px 11px' : '8px 15px',
              fontSize: compact ? 13 : 13.5,
              fontWeight: 700,
              background: on ? color.white : 'transparent',
              color: on ? color.blueDeep : color.muted,
              boxShadow: on ? '0 1px 6px rgba(18,38,63,0.10)' : 'none',
            }}
          >
            {t.label}
            {t.badge !== undefined && t.badge > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1,
                  padding: '4px 7px',
                  borderRadius: 999,
                  background: on ? color.tint : color.line,
                  color: on ? color.blueDeep : color.muted,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
